import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import GlobalAppBar from './GlobalAppBar';
import { appConfig } from '../config';
import { getSecureMediaUrl } from '../utils/mediaUtils';
import BottomNavigation from './BottomNavigation';
import GlobalChatWidget from './GlobalChatWidget';
import useNotifications from './useNotifications'; // eslint-disable-line no-unused-vars
import { useNotificationsContext } from './NotificationsContext';
import NotificationToast from './NotificationToast';
import './Dashboard.css';

// --- NEU: Helper Funktion für Mehrsprachigkeit ---
const getLocalizedContent = (item, field, langCode) => {
  if (!item) return '';
  const code = langCode ? String(langCode).split('-')[0].toLowerCase() : 'de';
  const localizedField = `${field}_${code}`;
  
  // Prüfen ob das feld_lang (z.B. name_en) existiert und nicht leer/null ist
  if (item[localizedField] && String(item[localizedField]).trim() !== '') {
    return item[localizedField];
  }
  // Fallback auf Default Feld (z.B. name)
  return item[field] || '';
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // 'user' hinzufügen
  const { user, makeAuthenticatedRequest, toggleFavourites, setProgress, getFeed, searchByTerm } = useAuth();
  
  // currentLanguage hier holen
  const { t, currentLanguage } = useLanguage();

  // ── Push-Benachrichtigungen ──────────────────────────────────────────────
  const { notifications, dismissOne, dismissAll } = useNotificationsContext();

  const [globalChatOpen,     setGlobalChatOpen]     = useState(false);
  const [chatInitialMessage, setChatInitialMessage] = useState(null);
  const [restoreChatOnOpen,  setRestoreChatOnOpen]  = useState(false);
  
  const [feedData, setFeedData] = useState([]);
  const [allFeedData, setAllFeedData] = useState([]);
  const [standaloneElements, setStandaloneElements] = useState([]);
  const [favourites, setFavourites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeNavItem, setActiveNavItem] = useState('Home');
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [metaAllTags, setMetaAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef(null);
  const feedContainerRef = useRef(null);
  const observerRef = useRef(null);
  const isFetchingRef = useRef(false);
  const fetchIdRef = useRef(0);
  const hasMoreRef = useRef(true);
  const loadingRef = useRef(true);
  const fetchFeedDataRef = useRef(null);
  const incrementPendingRef = useRef(false);
  const isSearchActiveRef = useRef(false);
  const showMandatoryOnlyRef = useRef(false);
  const isMandatoryMountedRef = useRef(false); // skip initial mount trigger
  const showFavouritesOnlyRef = useRef(false);
  const isFavouritesMountedRef = useRef(false);

  // Sync all mirror-refs after every render
  React.useLayoutEffect(() => {
    hasMoreRef.current = hasMore;
    loadingRef.current = loading;
    isSearchActiveRef.current = isSearchActive;
    showMandatoryOnlyRef.current = showMandatoryOnly;
    showFavouritesOnlyRef.current = showFavouritesOnly;
  });

  // Callback ref for the sentinel div – wires/unwires the IntersectionObserver
  // automatically whenever the element conditionally mounts or unmounts.
  // This replaces the static ref + separate useEffect[handleIntersect] pattern
  // which failed because the div didn't exist yet when the effect first ran.
  const loadMoreCallbackRef = useCallback((node) => {
    // Disconnect previous observer if any
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    loadMoreRef.current = node;
    if (!node) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (
          first.isIntersecting &&
          hasMoreRef.current &&
          !loadingRef.current &&
          !isSearchActiveRef.current &&
          !isFetchingRef.current &&
          !incrementPendingRef.current
        ) {
          incrementPendingRef.current = true;
          setPage(prev => prev + 1);
        }
      },
      { root: null, rootMargin: '200px', threshold: 0 }
    );
    observerRef.current.observe(node);
  }, []); // stable – reads all live values from refs
  const [isTwoColumnLayout, setIsTwoColumnLayout] = useState(true);
  const [showMandatoryOnly, setShowMandatoryOnly] = useState(false);
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(false);

  // Helper function to calculate collection progress based on elements
  const calculateCollectionProgress = (item) => {
    if (!item.isCollection || !Array.isArray(item.elements) || item.elements.length === 0) {
      return parseFloat(item.progress || 0);
    }
    const totalProgress = item.elements.reduce((sum, el) => {
      return sum + parseFloat(el.progress || 0);
    }, 0);
    return Math.round(totalProgress / item.elements.length);
  };

  useEffect(() => {
    const savedLayout = localStorage.getItem('hejvi_display_layout');
    if (savedLayout !== null) {
      setIsTwoColumnLayout(savedLayout === 'two-column');
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('hejvi_display_layout', isTwoColumnLayout ? 'two-column' : 'single-column');
  }, [isTwoColumnLayout]);

  // Language change: invalidate any in-flight fetch, reset state, then
  // trigger a fresh page-1 load via the ref (avoids TDZ since fetchFeedData
  // is declared as const later in the component body).
  useEffect(() => {
    fetchIdRef.current++;
    isFetchingRef.current = false;
    incrementPendingRef.current = false;
    setHasMore(true);
    setAllFeedData([]);
    setFeedData([]);
    setStandaloneElements([]);
    setPage(1);
    fetchFeedDataRef.current?.(1, selectedTags, true);
    fetchFavourites();
  }, [currentLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location?.state?.clearFilters) {
      setSelectedTags([]);
      setSearchQuery('');
    }
  }, [location?.state?.clearFilters]);

  useEffect(() => {
    if (location?.state?.fromVoiceSearch && location?.state?.voiceSearchResults) {
      console.log('Dashboard received voice search results:', location.state.voiceSearchResults);
      handleVoiceSearchResults(location.state.voiceSearchResults);
      navigate('/dashboard', { replace: true });
    }
  }, [location?.state?.fromVoiceSearch, location?.state?.voiceSearchResults]);

  // Custom Event: VoiceRecorder hat eine RAG-Antwort geholt und signalisiert
  // das über window, da BottomNavigation den Callback nicht durchreicht.
  useEffect(() => {
    const handler = (e) => {
      const { answer, sources, transcript } = e.detail || {};
      if (answer && transcript) {
        setChatInitialMessage({ question: transcript, answer, sources: sources || [] });
        setGlobalChatOpen(true);
      }
    };
    window.addEventListener('hejvi:voice-rag-answer', handler);
    return () => window.removeEventListener('hejvi:voice-rag-answer', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Tag filter change: same pattern – invalidate, reset, fetch page 1.
  useEffect(() => {
    if (searchQuery.trim()) return;
    fetchIdRef.current++;
    isFetchingRef.current = false;
    incrementPendingRef.current = false;
    setLoading(true);
    setHasMore(true);
    setAllFeedData([]);
    setFeedData([]);
    setStandaloneElements([]);
    setPage(1);
    fetchFeedDataRef.current?.(1, selectedTags, true);
    try { feedContainerRef.current && (feedContainerRef.current.scrollTop = 0); } catch(_) {}
  }, [selectedTags]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mandatory filter change: full reload so lazy-loaded pages don't cause gaps.
  useEffect(() => {
    if (!isMandatoryMountedRef.current) {
      isMandatoryMountedRef.current = true;
      return; // skip initial mount
    }
    if (searchQuery.trim()) return;
    fetchIdRef.current++;
    isFetchingRef.current = false;
    incrementPendingRef.current = false;
    setLoading(true);
    setHasMore(true);
    setAllFeedData([]);
    setFeedData([]);
    setStandaloneElements([]);
    setPage(1);
    fetchFeedDataRef.current?.(1, selectedTags, true);
    try { feedContainerRef.current && (feedContainerRef.current.scrollTop = 0); } catch(_) {}
  }, [showMandatoryOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // Favourites filter change: full reload so server applies the filter correctly.
  useEffect(() => {
    if (!isFavouritesMountedRef.current) {
      isFavouritesMountedRef.current = true;
      return;
    }
    if (searchQuery.trim()) return;
    fetchIdRef.current++;
    isFetchingRef.current = false;
    incrementPendingRef.current = false;
    setLoading(true);
    setHasMore(true);
    setAllFeedData([]);
    setFeedData([]);
    setStandaloneElements([]);
    setPage(1);
    fetchFeedDataRef.current?.(1, selectedTags, true);
    try { feedContainerRef.current && (feedContainerRef.current.scrollTop = 0); } catch(_) {}
  }, [showFavouritesOnly]); // eslint-disable-line react-hooks/exhaustive-deps
  // Page-1 loads are exclusively handled by the reset effects above.
  useEffect(() => {
    if (!searchQuery.trim() && page > 1) {
      incrementPendingRef.current = false; // allow next observer firing after this fetch completes
      fetchFeedDataRef.current?.(page, selectedTags, false);
    }
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- FIX: Suche wiederherstellen + Chat nach Element-Rückkehr wiederherstellen ---
  useEffect(() => {
    // Rückkehr von ElementPage mit Chat-Restore-Signal
    if (location.state?.restoreChat) {
      setGlobalChatOpen(true);
      setRestoreChatOnOpen(true);
      // State einmalig konsumieren, damit Reload nicht erneut triggert
      window.history.replaceState({}, '', location.pathname);
    }

    if (location.state?.searchContext) {
      const { query, active, tags } = location.state.searchContext;
      
      // Werte setzen
      if (tags) setSelectedTags(tags);
      if (query) setSearchQuery(query);
      if (active) setIsSearchActive(true);

      // WICHTIG: Suche im Dashboard erneut auslösen, wenn ein Query da war
      if (active && query) {
        // Wir rufen die Suche manuell auf, damit Ergebnisse da sind
        // (Kleines Timeout, damit State vorher gesetzt ist)
        setTimeout(() => handleSearchSubmit(query), 50);
      }
    }
  }, [location.state]);

  // (Observer is set up via loadMoreCallbackRef below)

  const fetchFeedData = async (pageNum, tagsParam, forceRefresh = false) => {
    // Explicit params prevent stale-closure reads of page/selectedTags.
    // Fall back to current state only if called without args (legacy path).
    const pNum = pageNum !== undefined ? pageNum : page;
    const tags = tagsParam !== undefined ? tagsParam : selectedTags;

    // Guard: reject concurrent calls unless this is a forced reset.
    if (isFetchingRef.current && !forceRefresh) return;

    // Claim a unique ID for this request. Any earlier in-flight fetch
    // will see its ID is outdated and discard its result.
    const myFetchId = ++fetchIdRef.current;

    try {
      isFetchingRef.current = true;
      setLoading(true);
      
      const raw = await getFeed({
        page: pNum,
        tags: tags.length > 0 ? tags : undefined,
        mandatory: showMandatoryOnlyRef.current ? 1 : undefined,
        favourites_only: showFavouritesOnlyRef.current ? 1 : undefined
      });

      // Stale-response check: a newer fetch was started while we were awaiting.
      if (myFetchId !== fetchIdRef.current) return;

      let list = [];
      if (raw?.status === 'success' && Array.isArray(raw?.data)) {
        list = raw.data;
      } else if (Array.isArray(raw)) {
        list = raw;
      } else if (Array.isArray(raw?.data)) {
        list = raw.data;
      }

      const apiAllTags = raw?.meta?.all_tags || [];
      if (apiAllTags.length > 0) {
        setMetaAllTags(apiAllTags);
      }

      const received = Array.isArray(list) ? list.length : 0;
      setHasMore(received > 0);

      // --- ÄNDERUNG: DATEN HIER ÜBERSETZEN ---
      const localizedList = list.map(item => ({
        ...item,
        // Hier übersetzen wir Name und Beschreibung direkt beim Laden
        name: getLocalizedContent(item, 'name', currentLanguage) || item.name || item.title,
        title: getLocalizedContent(item, 'name', currentLanguage) || item.title || item.name,
        description: getLocalizedContent(item, 'description', currentLanguage) || item.description,
        url_element: item.url_element || item.url || '', 
        is_author: user?.id && item.author ? String(item.author) === String(user.id) : false,
        subtitles: item.subtitles || null,
      }));

      if (pNum === 1 || forceRefresh) {
        setAllFeedData(localizedList);
        setFeedData(localizedList);
        // Standalone elements (nur Seite 1 vom Server geliefert)
        const rawStandalone = Array.isArray(raw?.standalone_elements) ? raw.standalone_elements : [];
        const localizedStandalone = rawStandalone.map(item => ({
          ...item,
          name:        getLocalizedContent(item, 'name',        currentLanguage) || item.name  || item.title,
          title:       getLocalizedContent(item, 'name',        currentLanguage) || item.title || item.name,
          description: getLocalizedContent(item, 'description', currentLanguage) || item.description,
          url_element: item.url_element || item.url || '',
          is_author:   user?.id && item.author ? String(item.author) === String(user.id) : false,
          subtitles:   item.subtitles || null,
        }));
        setStandaloneElements(localizedStandalone);
      } else {
        // Use functional updater to avoid stale-closure of allFeedData
        setAllFeedData(prev => [...prev, ...localizedList]);
        setFeedData(prev => [...prev, ...localizedList]);
      }

      
    } catch (error) {
      console.error('Failed to fetch feed data:', error);
      if (myFetchId !== fetchIdRef.current) return; // discard stale error too
      if (pNum === 1) {
        setFeedData([]);
        setAllFeedData([]);
      }
    } finally {
      // Only release the lock if we are still the active fetch.
      if (myFetchId === fetchIdRef.current) {
        setLoading(false);
        isFetchingRef.current = false;
      }
    }
  };
  // Register the latest version in the ref so effects declared earlier in the
  // component body can call it without hitting the temporal dead zone.
  fetchFeedDataRef.current = fetchFeedData;

  // --- ÄNDERUNG: TAGS ÜBERSETZEN ---
  useEffect(() => {
    // Tags basierend auf Sprache wählen
    const displayed = (Array.isArray(metaAllTags) ? metaAllTags : [])
      .filter(tg => Number(tg?.type) === 1) 
      .map(tg => {
          // Nutze Helper für Tag-Namen
          const translated = getLocalizedContent(tg, 'name', currentLanguage);
          // Fallback Logik aus Original Code (german_name vs name)
          if (!translated && currentLanguage === 'de') return tg.german_name || tg.name;
          return translated || tg.name;
      })
      .filter(Boolean);

    const unique = Array.from(new Set(displayed)).sort((a, b) => a.localeCompare(b));
    
    if (unique.length > 0 || !isSearchActive) {
      setAvailableTags(unique);
    }
  }, [metaAllTags, currentLanguage, isSearchActive]);


  const fetchFavourites = async () => {
    try {
      const res  = await makeAuthenticatedRequest('/favourites');
      // makeAuthenticatedRequest may return a raw Response or already-parsed JSON
      const data = (res && typeof res.json === 'function') ? await res.json() : res;

      let favouritesList = [];
      if (data?.status === 'success' && data?.data) {
        const collections = data.data.collections || [];
        const elements    = data.data.elements    || [];

        favouritesList = [
          ...collections.map(item => ({ ...item, type: 'collection' })),
          ...elements.map(item    => ({ ...item, type: 'element'    })),
        ];
      }
      setFavourites(favouritesList);
    } catch (error) {
      console.error('Failed to fetch favourites:', error);
      setFavourites([]);
    }
  };

  const toggleFavorite = async (elementId) => {
    try {
      const applyUpdate = (prev, serverFav = null) =>
        (Array.isArray(prev) ? prev : []).map(item => {
          const itemId = item.hash_id ?? item.hashId ?? item.id;
          if (itemId !== elementId) return item;
          const currentFav = Number(item?.favourite_value) === 1;
          const newFav = serverFav !== null ? serverFav : (currentFav ? 0 : 1);
          return { ...item, isFavorite: newFav === 1, favourite_value: newFav };
        });

      // Optimistisches Update auf beiden Listen
      setFeedData(prev => applyUpdate(prev));
      setStandaloneElements(prev => applyUpdate(prev));

      const res = await toggleFavourites(elementId);
      if (res && res.data && typeof res.data.favourite_value !== 'undefined') {
        const serverFav = Number(res.data.favourite_value) === 1 ? 1 : 0;
        setFeedData(prev => applyUpdate(prev, serverFav));
        setStandaloneElements(prev => applyUpdate(prev, serverFav));
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      // Revert: erneut togglen (optimistisch) → zurück auf Original
      const revert = (prev) =>
        (Array.isArray(prev) ? prev : []).map(item => {
          const itemId = item.hash_id ?? item.hashId ?? item.id;
          if (itemId !== elementId) return item;
          const currentFav = Number(item?.favourite_value) === 1;
          return { ...item, isFavorite: currentFav, favourite_value: currentFav ? 1 : 0 };
        });
      setFeedData(prev => revert(prev));
      setStandaloneElements(prev => revert(prev));
    }
  };

  const handleSearchToggle = () => {
    setIsSearchActive(!isSearchActive);
    if (isSearchActive) {
      setSearchQuery('');
      if (allFeedData.length > 0) {
        setFeedData(allFeedData);
      }
    }
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    if (allFeedData.length > 0) {
      setFeedData(allFeedData);
    }
  };

  const handleSearchQuery = (query) => {
    setSearchQuery(query);
  };

  const handleSearchSubmit = async (query) => {
    if (!query.trim()) {
      if (allFeedData.length > 0) {
        setFeedData(allFeedData);
      } else {
        await fetchFeedData();
      }
      return;
    }
    
    try {
      setLoading(true);
      const result = await searchByTerm(query, { limit: 20 });
      
      const feedResponse = await getFeed();
      const feedDataRaw = feedResponse?.data || [];
      
      const feedMap = new Map();
      const nameMap = new Map();
      feedDataRaw.forEach(item => {
        feedMap.set(item.hash_id, item);
        if (item.name) {
          nameMap.set(item.name.toLowerCase(), item);
        }
      });
      
      const normalizeSearch = (data) => {
        if (!data) return [];
        const collections = Array.isArray(data?.collections) ? data.collections : [];
        const elements = Array.isArray(data?.elements) ? data.elements : [];
        
        const mapCollection = (c) => {
          let feedItem = feedMap.get(c.hash_id);
          if (!feedItem && c.name) feedItem = nameMap.get(c.name.toLowerCase());
          const searchResultImage = c.url_thumbnail || c.image || c.thumbnail || c.thumb_url;
          
          return {
            ...c,
            id: c.hash_id ?? c.id,
            // --- ÜBERSETZUNG HIER ---
            name: getLocalizedContent(c, 'name', currentLanguage) || c.name,
            title: getLocalizedContent(c, 'name', currentLanguage) || c.name,
            description: getLocalizedContent(c, 'description', currentLanguage) || c.description,
            // ------------------------
            image: feedItem?.url_thumbnail || searchResultImage,
            url_thumbnail: feedItem?.url_thumbnail || searchResultImage,
            url_element: feedItem?.url_element || feedItem?.url || c.url_element || c.url || '',
            subtitles: feedItem?.subtitles || c.subtitles || null,
            progress: parseFloat(c.progress || 0),
            isCollection: true,
            isFavorite: c.favourite_value === 1,
            favourite_value: c.favourite_value || 0,
            type: 1, 
            elements: (c.elements || []).map(el => ({
              ...el,
              isFavorite: el.favourite_value === 1,
              progress: parseFloat(el.progress || 0)
            }))
          };
        };
        
        const mapElement = (e) => {
          let feedItem = feedMap.get(e.hash_id);
          if (!feedItem && e.name) feedItem = nameMap.get(e.name.toLowerCase());
          const searchResultImage = e.url_thumbnail || e.image || e.thumbnail || e.thumb_url;
          
          return {
            ...e,
            id: e.hash_id ?? e.id,
            // --- ÜBERSETZUNG HIER ---
            name: getLocalizedContent(e, 'name', currentLanguage) || e.name,
            title: getLocalizedContent(e, 'name', currentLanguage) || e.name,
            description: getLocalizedContent(e, 'description', currentLanguage) || e.description,
            // ------------------------
            image: feedItem?.url_thumbnail || searchResultImage,
            url_thumbnail: feedItem?.url_thumbnail || searchResultImage,
            url_element: feedItem?.url_element || feedItem?.url || e.url_element || e.url || '',
            subtitles: feedItem?.subtitles || e.subtitles || null,
            progress: parseFloat(e.progress || 0),
            isCollection: false,
            type: 2, 
            isFavorite: e.favourite_value === 1,
            favourite_value: e.favourite_value || 0
          };
        };
        
        return [
          ...collections.map(mapCollection),
          ...elements.map(mapElement)
        ];
      };
      
      const list = normalizeSearch(result.data || result);
      setFeedData(list);
    } catch (error) {
      console.error('Search failed:', error);
      if (allFeedData.length > 0) {
        setFeedData(allFeedData);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceSearchResults = async (results) => {
    // 1. Begriff extrahieren (falls Objekt übergeben wird)
    const query = results?.transcript || (typeof results === 'string' ? results : '');

    if (query) {
      // 2. UI sofort aktualisieren
      setSearchQuery(query);
      setIsSearchActive(true);
      
      // 3. WICHTIG: Die echte Suche auslösen!
      // Damit nutzen wir exakt dieselbe Logik wie beim Tippen + Enter.
      await handleSearchSubmit(query);
    }
  };

  /**
   * Wird aufgerufen wenn das Backend eine RAG-Antwort auf eine Sprachfrage zurückgibt.
   * Öffnet das GlobalChatWidget und injiziert Frage + Antwort direkt als Chat-Verlauf.
   */
  const handleVoiceRagAnswer = ({ answer, sources, transcript }) => {
    setChatInitialMessage({ question: transcript, answer, sources: sources || [] });
    setGlobalChatOpen(true);
  };

  const handleDisplayToggle = () => {
    setIsTwoColumnLayout(!isTwoColumnLayout);
  };

  const filteredFeedData = (Array.isArray(feedData) ? feedData : [])
    .filter(item => !showMandatoryOnly || Number(item?.is_mandatory) === 1);

  // Standalone elements: bei aktiver Suche oder Pflicht-Filter nicht anzeigen
  const filteredStandaloneElements = (!isSearchActive && !showMandatoryOnly)
    ? standaloneElements
    : [];

  // ── Gemeinsamer Card-Renderer (Collections + Standalone Elements) ──────────
  const renderFeedCard = (item) => {
    const hashId = item?.hash_id ?? item?.hashId ?? null;
    const routeId = hashId || item?.id || 'local';
    const searchContext = { query: searchQuery, active: isSearchActive, tags: selectedTags };
    const isCollection = (
      Number(item?.type) === 1 ||
      item?.type === 'collection' ||
      Array.isArray(item?.elements) ||
      item?.isCollection === true ||
      Number(item?.is_collection) === 1 ||
      item?.kind === 'collection'
    );
    let image = item?.url_thumbnail || item?.image;
    if (image && image.includes('app.hejvi.de/img/') && !image.includes('/api/img/')) {
      image = image.replace('app.hejvi.de/img/', 'app.hejvi.de/api/img/');
    }
    const title = item?.name || item?.title;

    const handleCardClick = async (e) => {
      if (e.target.closest('.feed-fav') || e.target.closest('.feed-play')) return;
      if (!hashId && !isCollection) return;
      if (parseFloat(item?.progress) === 0) {
        try { await setProgress(hashId, 1); } catch (_) {}
      }
      if (hashId && hashId.startsWith('el-')) {
        if (appConfig?.workflow?.directVideoPlay) {
          navigate(`/element-feed/${hashId}`, { state: { lessons: [item], startIndex: 0, collectionData: null, sourcePage: '/dashboard', searchContext } });
        } else {
          navigate(`/element/${hashId}`, { state: { elementData: item, sourcePage: '/dashboard', searchContext } });
        }
      } else if (hashId && hashId.startsWith('col-')) {
        navigate(`/course/${hashId}`, { state: { collectionData: item, sourcePage: '/dashboard', searchContext } });
      } else {
        if (isCollection) {
          navigate(`/course/${routeId}`, { state: { collectionData: item, sourcePage: '/dashboard', searchContext } });
        } else {
          navigate(`/element/${hashId}`);
        }
      }
    };

    return (
      <div
        key={hashId || 'unknown-' + Math.random()}
        className="feed-card"
        onClick={handleCardClick}
      >
        <div className="feed-media" style={{
          backgroundImage: image ? `url(${getSecureMediaUrl(image)})` : 'none',
          backgroundColor: image ? 'transparent' : 'rgba(255,255,255,0.05)'
        }}>
          {Number(item?.is_mandatory) === 1 && (
            <div className="feed-badge">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#FFFFFF" strokeWidth="2"/>
                <path d="M7 12.5L10.2 15.7L17 9" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{t('mandatory')}</span>
            </div>
          )}

          <button
            className="feed-play"
            onClick={(e) => {
              e.stopPropagation();
              if (!hashId && !isCollection) return;
              if (hashId && hashId.startsWith('el-')) {
                navigate(`/element/${hashId}`, { state: { elementData: item, sourcePage: '/dashboard', searchContext } });
              } else if (hashId && hashId.startsWith('col-')) {
                navigate(`/course/${routeId}`, { state: { collectionData: item, sourcePage: '/dashboard', searchContext } });
              } else {
                if (isCollection) {
                  navigate(`/course/${routeId}`, { state: { collectionData: item, sourcePage: '/dashboard', searchContext } });
                } else {
                  navigate(`/element/${hashId}`, { state: { elementData: item, sourcePage: '/dashboard', searchContext } });
                }
              }
            }}
          >
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
              <circle cx="40" cy="40" r="40" fill="rgba(255,255,255,0.95)"/>
              <path d="M32 25L55 40L32 55V25Z" fill="#FF6407"/>
            </svg>
          </button>

          <button
            className={`feed-fav ${Number(item?.favourite_value) === 1 ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              hashId && toggleFavorite(hashId);
              return false;
            }}
            onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
            aria-label="toggle-favourite"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 17.3L6.5 20.5L7.9 14.2L3 9.9L9.4 9L12 3L14.6 9L21 9.9L16.1 14.2L17.5 20.5L12 17.3Z"
                fill={Number(item?.favourite_value) === 1 ? '#FF6407' : '#A3A3A3'} />
            </svg>
          </button>

          <div className="feed-footer">
            {title && <h3 className="feed-title">{title}</h3>}
            {(() => {
              const progressValue = calculateCollectionProgress(item);
              return progressValue > 0 && (
                <div className="feed-progress">
                  <div className="feed-progress-fill" style={{ width: `${progressValue}%` }}></div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    );
  };
  

  if (loading && page === 1) {
    return (
      <div className="dashboard-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('loadingCourses') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="dashboard-container">
        {/* Global App Bar */}
        <GlobalAppBar 
          title="HeyVi"
          showSearch={true}
          showDisplayToggle={true}
          isTwoColumnLayout={isTwoColumnLayout}
          onDisplayToggle={handleDisplayToggle}
          onSearchToggle={handleSearchToggle}
          onSearchQuery={handleSearchQuery}
          onSearchSubmit={handleSearchSubmit}
          searchQuery={searchQuery}
          isSearchActive={isSearchActive}
        />

        {/* Tag Filters + Chat Button */}
        <div className="tags-container-dashboard">
          <button
            className={`gcw-fab-inline favourites-filter-btn${showFavouritesOnly ? ' active' : ''}`}
            onClick={() => setShowFavouritesOnly(prev => !prev)}
            aria-label={t('favouritesFilter') || 'Favoriten'}
          >
            <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
              <path d="M12 17.3L6.5 20.5L7.9 14.2L3 9.9L9.4 9L12 3L14.6 9L21 9.9L16.1 14.2L17.5 20.5L12 17.3Z"
                fill={showFavouritesOnly ? '#fff' : '#374151'}
                stroke={showFavouritesOnly ? '#fff' : '#374151'}
                strokeWidth="1"
              />
            </svg>
            <span>{t('favourites') || 'Favoriten'}</span>
          </button>
          <div className="tags-scroll">

            {availableTags.map((tag) => {
              const isSelected = selectedTags.includes(tag);
              return (
                <button
                  key={tag}
                  className={`tag-button ${isSelected ? 'active' : ''}`}
                  onClick={() => {
                    const next = isSelected
                      ? selectedTags.filter(tg => tg !== tag)
                      : [...selectedTags, tag];
                    setSelectedTags(next);
                    setPage(1);
                  }}
                  title={`Filter by ${tag}`}
                >
                  #{tag}
                </button>
              );
            })}
          </div>
          <button
            className={`gcw-fab-inline mandatory-filter-btn${showMandatoryOnly ? ' active' : ''}`}
            onClick={() => setShowMandatoryOnly(prev => !prev)}
            aria-label={t('mandatoryFilter') || 'Pflichtkurse'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
              <circle cx="12" cy="12" r="10"/>
              <path d="M7 12.5L10.2 15.7L17 9" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{t('mandatory') || 'Pflichtkurse'}</span>
          </button>
          <button
            className="gcw-fab-inline"
            onClick={() => { setChatInitialMessage(null); setGlobalChatOpen(true); }}
            aria-label={t('globalChatTitle') || 'Knowledge Assistant'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              <circle cx="9" cy="10" r="1" fill="currentColor" stroke="none"/>
              <circle cx="12" cy="10" r="1" fill="currentColor" stroke="none"/>
              <circle cx="15" cy="10" r="1" fill="currentColor" stroke="none"/>
            </svg>
            <span>{t('globalChatFab') || 'Alles fragen'}</span>
          </button>
        </div>

        {/* Feed Content */}
        <div className="feed-container" ref={feedContainerRef}>
          {filteredFeedData.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
              <p>{t('noCourses') || 'No feed data available'}</p>
            </div>
          ) : (
            <>
            <div className={`feed-grid ${isTwoColumnLayout ? 'feed-grid-two-column' : 'feed-grid-single-column'}`}>
              {filteredFeedData.map((item) => renderFeedCard(item))}
            </div>
            
            {!isSearchActive && hasMore && (
              <div ref={loadMoreCallbackRef} style={{ padding: '20px', textAlign: 'center' }}>
                {loading && page > 1 ? (
                  <div className={`feed-grid ${isTwoColumnLayout ? 'feed-grid-two-column' : 'feed-grid-single-column'}`}>
                    {Array.from({ length: 4 }).map((_, idx) => (
                      <div key={`skeleton-${idx}`} className="feed-card skeleton">
                        <div className="feed-media skeleton-block"></div>
                        <div className="feed-footer">
                          <div className="skeleton-line title"></div>
                          <div className="skeleton-line small"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: '#6B7280', fontSize: '14px' }}>
                    {t('loadMore') || 'Scroll down to load more'}
                  </div>
                )}
              </div>
            )}

            {/* ── Standalone Elements Block ─────────────────────────────────
                Nur sichtbar wenn: keine Suche aktiv, kein Pflicht-Filter,
                und mindestens ein Element vorhanden.
            ────────────────────────────────────────────────────────────────── */}
            {filteredStandaloneElements.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{
                  padding: '16px 20px 8px',
                  display: 'flex', alignItems: 'baseline', gap: 10,
                }}>
                  <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#374151' }}>
                    {t('mc_section_videos') || 'Videos'}
                  </h2>
                  <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                    {filteredStandaloneElements.length}
                  </span>
                </div>
                <div className={`feed-grid ${isTwoColumnLayout ? 'feed-grid-two-column' : 'feed-grid-single-column'}`}>
                  {filteredStandaloneElements.map((item) => renderFeedCard(item))}
                </div>
              </div>
            )}
            </>
          )}
        </div>


        <NotificationToast
          notifications={notifications}
          onDismiss={dismissOne}
          onDismissAll={dismissAll}
          onNavigate={(path) => navigate(path)}
        />

        <BottomNavigation
          onVoiceSearchResults={handleVoiceSearchResults}
          onRagAnswer={handleVoiceRagAnswer}
        />

        <GlobalChatWidget
          isOpen={globalChatOpen}
          onClose={() => {
            setGlobalChatOpen(false);
            setRestoreChatOnOpen(false);
            // initialMessage nach dem Schließen verzögert löschen,
            // damit die Animation noch die Inhalte zeigt
            setTimeout(() => setChatInitialMessage(null), 300);
          }}
          initialMessage={chatInitialMessage}
          restoreFromSession={restoreChatOnOpen}
        />
      </div>
    </>
  );
};

export default Dashboard;